import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Send } from 'lucide-react';
import { toast } from 'sonner';

const FeedbackSection = ({ decisionId, onFeedbackSubmitted }) => {
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [helpful, setHelpful] = useState(null);
  const [showTextArea, setShowTextArea] = useState(false);
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleThumbClick = async (isHelpful) => {
    setHelpful(isHelpful);
    setShowTextArea(true);
    
    // Log the initial thumb click to backend
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/feedback`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            decision_id: decisionId,
            helpful: isHelpful,
            feedback_notes: '',
            timestamp: new Date().toISOString()
          })
        }
      );
    } catch (error) {
      console.error('Error logging initial feedback:', error);
    }
  };

  const handleSubmit = async () => {
    // Feedback notes are now optional - allow submit even if empty
    setSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/feedback`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            decision_id: decisionId,
            helpful,
            feedback_notes: feedbackNotes,
            timestamp: new Date().toISOString()
          })
        }
      );

      if (response.ok) {
        setFeedbackGiven(true);
        toast.success('Thank you for your feedback!');
        if (onFeedbackSubmitted) {
          onFeedbackSubmitted();
        }
      } else {
        toast.error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (feedbackGiven) {
    return (
      <div className="glass-card p-6 text-center" data-testid="feedback-thank-you">
        <p className="text-lg text-purple-600 font-semibold">
          Thank you for your feedback! 🙏
        </p>
        <p className="text-gray-600 mt-2">
          Your insights help improve the system.
        </p>
      </div>
    );
  }

  return (
    <div className="glass-card p-6" data-testid="feedback-section">
      <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        Was this analysis helpful?
      </h3>
      <p className="text-gray-600 mb-4">
        Your feedback helps the system learn your unique signals and improve future analyses.
      </p>

      {/* Helpful/Not Helpful */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => handleThumbClick(true)}
          style={{
            backgroundColor: helpful === true ? 'rgb(204, 251, 241)' : 'white',
            borderColor: helpful === true ? 'rgb(20, 184, 166)' : 'rgb(209, 213, 219)'
          }}
          onMouseEnter={(e) => {
            if (helpful !== true) {
              e.currentTarget.style.backgroundColor = 'rgb(153, 246, 228)';
              e.currentTarget.style.borderColor = 'rgb(13, 148, 136)';
            }
          }}
          onMouseLeave={(e) => {
            if (helpful !== true) {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = 'rgb(209, 213, 219)';
            }
          }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-lg border-2 transition-all font-medium ${
            helpful === true ? 'text-turquoise-700' : 'text-gray-700'
          }`}
          data-testid="feedback-helpful-btn"
        >
          <ThumbsUp className="w-5 h-5" />
          Helpful
        </button>
        <button
          onClick={() => handleThumbClick(false)}
          style={{
            backgroundColor: helpful === false ? 'rgb(252, 231, 243)' : 'white',
            borderColor: helpful === false ? 'rgb(236, 72, 153)' : 'rgb(209, 213, 219)'
          }}
          onMouseEnter={(e) => {
            if (helpful !== false) {
              e.currentTarget.style.backgroundColor = 'rgb(251, 207, 232)';
              e.currentTarget.style.borderColor = 'rgb(219, 39, 119)';
            }
          }}
          onMouseLeave={(e) => {
            if (helpful !== false) {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = 'rgb(209, 213, 219)';
            }
          }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-lg border-2 transition-all font-medium ${
            helpful === false ? 'text-pink-700' : 'text-gray-700'
          }`}
          data-testid="feedback-not-helpful-btn"
        >
          <ThumbsDown className="w-5 h-5" />
          Not Helpful
        </button>
      </div>

      {/* Feedback Notes - Only show after thumb click */}
      {showTextArea && (
        <div className="mb-6 fade-in">
          <label className="block text-sm font-semibold mb-2">
            What resonated or felt off? <span className="text-gray-500 font-normal">(optional)</span>
          </label>
          <textarea
            data-testid="feedback-notes-input"
            value={feedbackNotes}
            onChange={(e) => setFeedbackNotes(e.target.value)}
            className="textarea-field"
            placeholder="Share what felt accurate or what didn't quite land. This helps the system understand your unique decision-making patterns..."
            rows={4}
            autoFocus
          />
          
          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-primary w-full flex items-center justify-center gap-2 mt-3"
            data-testid="submit-feedback-btn"
          >
            {submitting ? (
              <>
                <div className="loading-spinner w-5 h-5"></div>
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit Feedback
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default FeedbackSection;
